import { Dispatch, SetStateAction, useEffect, useMemo, useState } from "react";
import {
  Button,
  Checkbox,
  Color,
  Flex,
  Icon,
  Input,
  Search,
  Select,
  SubgridTable,
  Text,
} from "src/elements";
import { MerchflowReviewUpdateStep, ProductUpdateAdd, ProductUpdates } from "../store/types";
import { useLoaders } from "src/components/InlineLoader";
import { RouteGetInputsProductsAll } from "@CommonApi/inputs/products/all";
import { PageStatus, Tooltip } from "src/components";
import { productToReadableFormat } from "../store/utils";
import { useModals } from "src/components/Modals";
import { FlowUpdateProduct } from "@CommonApi/merchflow/_merchflowId_/flow-update/products";
import { ProductToInsert } from "@CommonApi/merchflow/_merchflowId_/flow-update";

interface Props {
  addableProducts: FlowUpdateProduct[];
  removableProducts: FlowUpdateProduct[];
  setStep: (mode: MerchflowReviewUpdateStep) => void;
  setProductUpdates: Dispatch<SetStateAction<ProductUpdates>>;
}

type SelectableProductToAddType = {
  [key: string]: {
    isChecked: boolean;
    product: FlowUpdateProduct;
    adjacentPosition: "LEFT" | "RIGHT" | null;
    adjacentProduct: FlowUpdateProduct | null;
    facings: string;
    reducibleProduct: FlowUpdateProduct | null;
    fitStrategy: ProductToInsert["fitStrategy"];
    canBeSqueezed: boolean;
  };
};

export const Step3CAddProducts = ({
  addableProducts,
  removableProducts,
  setStep,
  setProductUpdates,
}: Props) => {
  const { openConfirmModal } = useModals();
  const { hasLoader } = useLoaders();

  const [selectedProducts, setSelectedProducts] = useState<SelectableProductToAddType>({});
  const [isModified, setIsModified] = useState(false);
  const [search, setSearch] = useState("");

  const isProductsLoading = hasLoader(RouteGetInputsProductsAll);

  useEffect(() => {
    if (!addableProducts) return;

    setSelectedProducts(() => {
      const predefinedSelectedProducts: SelectableProductToAddType = {};

      for (const product of addableProducts) {
        predefinedSelectedProducts[product.productCode] = {
          isChecked: false,
          product,
          adjacentPosition: null,
          adjacentProduct: null,
          facings: "",
          reducibleProduct: null,
          fitStrategy: "KEEP_ALL_FACINGS",
          canBeSqueezed: false,
        };
      }

      return predefinedSelectedProducts;
    });
  }, [addableProducts]);

  const onSave = () => {
    setProductUpdates((productUpdates) => [
      ...productUpdates,
      ...Object.values(selectedProducts)
        .filter((selectedProduct) => selectedProduct.isChecked)
        .map(
          (selectedProduct) =>
            ({
              type: "add",
              productToAdd: selectedProduct.product,
              adjacentPosition: selectedProduct.adjacentPosition,
              adjacentProduct: selectedProduct.adjacentProduct,
              facings: selectedProduct.facings,
              reducibleProduct: selectedProduct.reducibleProduct,
              fitStrategy: selectedProduct.fitStrategy,
              pnlDecision: "DEFAULT",
              storeCodes: [],
              canBeSqueezed: selectedProduct.canBeSqueezed,
            }) satisfies ProductUpdateAdd,
        ),
    ]);

    setStep("STEP_3_PRODUCT_REVIEW");
  };

  const openInformationModal = () => {
    openConfirmModal({
      title: "How we make space for added products",
      description: (
        <Flex column padding="10px" gap="5px">
          <Flex margin="0 0 10px 0">
            <Text variant="small1" color={Color.textMain}>
              We make sure there is enough space on the shelves for new products by doing these in
              this order:
            </Text>
          </Flex>

          <Text variant="small2">
            1. Decrease facings of products over minimum required dos and mos
          </Text>

          <Text variant="small2">
            2. Decrease facings of products such that we may dip under dos
          </Text>

          <Text variant="small2">
            3. Decrease facings of products such that we may dip under mos and dos
          </Text>

          <Flex margin="10px 0 0 0">
            <Text variant="small2">
              If the above were unsuccessful, then new products may not be added to stores.
            </Text>
          </Flex>
        </Flex>
      ),
      buttons: [{ id: "ok", label: "OK", color: "greenSmoke" }],
    });
  };

  const filteredSelectableProducts = useMemo(() => {
    return Object.values(selectedProducts)
      .filter((p) =>
        search.length > 0
          ? p.product.productCode.toLowerCase().includes(search.toLowerCase()) ||
            p.product.name.toLowerCase().includes(search.toLowerCase())
          : true,
      )
      .sort((p1, p2) => (p1.product.productCode < p2.product.productCode ? -1 : 1));
  }, [selectedProducts, search]);

  const noSelected = useMemo(() => {
    return Object.values(selectedProducts).find((p) => p.isChecked) === undefined;
  }, [selectedProducts]);

  const isAdjacencyInvalid = (row: (typeof selectedProducts)[string]) =>
    row.isChecked &&
    ((row.adjacentPosition && !row.adjacentProduct) ||
      (!row.adjacentPosition && row.adjacentProduct));

  const hasPartiallySelected = useMemo(() => {
    return Object.values(selectedProducts).find((p) => isAdjacencyInvalid(p)) !== undefined;
  }, [selectedProducts]);

  const isFacingsInvalid = (row: (typeof selectedProducts)[string]) =>
    row.isChecked && row.facings.trim() !== "" && !/^[1-9]\d*$/.test(row.facings.trim());

  const hasInvalidFacings = useMemo(() => {
    return Object.values(selectedProducts).some((p) => isFacingsInvalid(p));
  }, [selectedProducts]);

  return (
    <Flex column flexGrow gap="20px">
      <Flex column flexGrow gap="20px">
        <Text color={Color.primary} variant="h4">
          Please select an available product to be added. We will reduce facings of existing
          products to make room for this addition.
          <Flex display="inline-flex" position="relative">
            <Flex position="absolute" bottom="4px" onClick={openInformationModal}>
              <Icon name="info" color={Color.primary} size={12} />
              <Tooltip>How do we do that?</Tooltip>
            </Flex>
          </Flex>
        </Text>

        {isProductsLoading ? (
          <PageStatus icon="spinner" label="Loading available products..." />
        ) : (
          <Flex column flexGrow gap="10px">
            <Search width="200px" search={search} setSearch={setSearch} />

            <SubgridTable
              virtualRowHeight={52}
              columns={[
                {
                  id: "selected",
                  header: "",
                  width: "40px",
                  justify: "center",
                  align: "center",
                  renderer: (data) => (
                    <Checkbox
                      name="addable-product"
                      isChecked={selectedProducts[data.row.product.productCode].isChecked}
                      setIsChecked={(isChecked) => {
                        setSelectedProducts((selectedProducts) => ({
                          ...selectedProducts,
                          [data.row.product.productCode]: {
                            ...selectedProducts[data.row.product.productCode],
                            isChecked,
                          },
                        }));
                        setIsModified(true);
                      }}
                    />
                  ),
                },
                {
                  header: "Addables",
                  id: "product_code",
                  align: "center",
                  width: "200px",
                  renderer: (data) => (
                    <Text lineClamp={2} variant="small2">
                      {productToReadableFormat(data.row.product)}
                    </Text>
                  ),
                },
                {
                  id: "adjacent_position",
                  header: "Adjacency",
                  align: "center",
                  renderer: (data) => (
                    <Flex maxWidth="180px">
                      <Flex
                        border={`2px solid ${
                          isAdjacencyInvalid(data.row) ? Color.red : Color.transparent
                        }`}
                        borderRadius="4px"
                        padding="2px"
                        maxWidth="100%"
                      >
                        <Select
                          value={data.row.adjacentPosition}
                          setValue={(value: "LEFT" | "RIGHT" | null) => {
                            setSelectedProducts((selectedProducts) => ({
                              ...selectedProducts,
                              [data.row.product.productCode]: {
                                ...selectedProducts[data.row.product.productCode],
                                adjacentPosition: value,
                              },
                            }));
                            setIsModified(true);
                          }}
                          options={[
                            { label: "None", value: null },
                            { label: "LEFT", value: "LEFT" },
                            {
                              label: "RIGHT",
                              value: "RIGHT",
                            },
                          ]}
                          isDisabled={!data.row.isChecked}
                        />
                      </Flex>
                      {isAdjacencyInvalid(data.row) && (
                        <Tooltip position="top">
                          Both adjacency fields must be filled or empty
                        </Tooltip>
                      )}
                    </Flex>
                  ),
                },
                {
                  id: "adjacent_product",
                  header: "Adjacent Product",
                  width: "200px",
                  align: "center",
                  renderer: (data) => (
                    <Flex maxWidth="180px">
                      <Flex
                        border={`2px solid ${
                          isAdjacencyInvalid(data.row) ? Color.red : Color.transparent
                        }`}
                        borderRadius="4px"
                        padding="2px"
                        maxWidth="100%"
                      >
                        <Select
                          value={selectedProducts[data.row.product.productCode].adjacentProduct!}
                          setValue={(value) => {
                            setSelectedProducts((selectedProducts) => ({
                              ...selectedProducts,
                              [data.row.product.productCode]: {
                                ...selectedProducts[data.row.product.productCode],
                                adjacentProduct: value,
                              },
                            }));
                            setIsModified(true);
                          }}
                          options={[
                            { label: "None", value: null },
                            ...(removableProducts || []).map((product) => ({
                              label: productToReadableFormat(product),
                              value: product,
                            })),
                          ]}
                          isSearchable
                          isDisabled={!data.row.isChecked}
                        />
                      </Flex>
                      {isAdjacencyInvalid(data.row) && (
                        <Tooltip position="top">
                          Both adjacency fields must be filled or empty
                        </Tooltip>
                      )}
                    </Flex>
                  ),
                },
                {
                  header: "Facings",
                  id: "facings",
                  width: "80px",
                  align: "center",
                  renderer: (data) => {
                    const row = data.row;
                    const isInvalid = isFacingsInvalid(row);
                    return (
                      <Flex maxWidth="100%">
                        <Flex
                          border={`2px solid ${isInvalid ? Color.red : Color.transparent}`}
                          borderRadius="4px"
                          padding="2px"
                          maxWidth="100%"
                        >
                          <Input
                            placeholder="Auto"
                            value={data.row.facings}
                            setValue={(value) => {
                              setSelectedProducts((selectedProducts) => ({
                                ...selectedProducts,
                                [data.row.product.productCode]: {
                                  ...selectedProducts[data.row.product.productCode],
                                  facings: value,
                                },
                              }));
                              setIsModified(true);
                            }}
                            isDisabled={!data.row.isChecked}
                          />
                        </Flex>
                        {isInvalid && (
                          <Tooltip position="top">Facings must be a positive integer</Tooltip>
                        )}
                      </Flex>
                    );
                  },
                },
                {
                  header: "Product to reduce",
                  id: "reducible_product",
                  width: "200px",
                  align: "center",
                  renderer: (data) => (
                    <Select
                      value={selectedProducts[data.row.product.productCode].reducibleProduct}
                      setValue={(value) => {
                        setSelectedProducts((selectedProducts) => ({
                          ...selectedProducts,
                          [data.row.product.productCode]: {
                            ...selectedProducts[data.row.product.productCode],
                            reducibleProduct: value,
                          },
                        }));
                        setIsModified(true);
                      }}
                      options={[
                        { label: "None", value: null },
                        ...(removableProducts || []).map((product) => ({
                          label: productToReadableFormat(product),
                          value: product,
                        })),
                      ]}
                      isSearchable
                      isDisabled={!data.row.isChecked}
                    />
                  ),
                },
                {
                  header: "Fit Strategy",
                  id: "fit_strategy",
                  width: "250px",
                  align: "center",
                  renderer: (data) => (
                    <Select
                      value={selectedProducts[data.row.product.productCode].fitStrategy}
                      setValue={(fitStrategy) => {
                        setSelectedProducts((selectedProducts) => ({
                          ...selectedProducts,
                          [data.row.product.productCode]: {
                            ...selectedProducts[data.row.product.productCode],
                            fitStrategy: fitStrategy as ProductToInsert["fitStrategy"],
                          },
                        }));
                        setIsModified(true);
                      }}
                      options={[
                        {
                          label: "No other products to be reduced",
                          value: "KEEP_ALL_FACINGS",
                        },
                        {
                          label: "Any product could be reduced",
                          value: "CAN_REDUCE_FACINGS",
                        },
                        {
                          label: "Any non-core product could be reduced / deleted",
                          value: "CAN_DELETE_RANGED_PRODUCTS",
                        },
                        {
                          label: "Any product could be reduced / deleted",
                          value: "CAN_DELETE_RANGED_PRODUCTS_INCLUDING_CORE_RANGE",
                        },
                      ]}
                      isDisabled={!data.row.isChecked}
                    />
                  ),
                },
                {
                  header: "Squeezed",
                  id: "can_be_squeezed",
                  width: "80px",
                  align: "center",
                  justify: "center",
                  renderer: (data) => (
                    <Flex maxWidth="100%" justify="center" align="center">
                      <Checkbox
                        name="can-be-squeezed"
                        isChecked={selectedProducts[data.row.product.productCode].canBeSqueezed}
                        setIsChecked={(canBeSqueezed) => {
                          setSelectedProducts((selectedProducts) => ({
                            ...selectedProducts,
                            [data.row.product.productCode]: {
                              ...selectedProducts[data.row.product.productCode],
                              canBeSqueezed,
                            },
                          }));
                          setIsModified(true);
                        }}
                        isDisabled={!data.row.isChecked}
                      />
                    </Flex>
                  ),
                },
              ]}
              data={Object.values(filteredSelectableProducts)}
            />
          </Flex>
        )}
      </Flex>

      <Flex justify="between" gap="10px">
        <Button
          color={isModified ? "red" : "greenSmoke"}
          variant="inverted"
          onClick={() => setStep("STEP_3_PRODUCT_REVIEW")}
        >
          {isModified ? "Cancel" : "Back"}
        </Button>

        <Button
          isDisabled={noSelected || hasPartiallySelected || hasInvalidFacings}
          onClick={onSave}
        >
          Save
        </Button>
      </Flex>
    </Flex>
  );
};
